/**
 * Client-side booking validation.
 *
 * This is a mirror of `backend/utils/bookingValidation.js`. The server is the authority —
 * this copy exists so the guest gets an instant, specific message instead of a round trip.
 * Keep the two files in step when a rule changes.
 */

// Indian Railways PNR: exactly 10 digits.
export const PNR_LENGTH = 10;
const PNR_REGEX = /^\d{10}$/;

// Country dial codes offered at the counter. India first — it is the default.
// `nsn` is the expected National Significant Number length(s) for that country.
export const COUNTRY_CODES = [
  { code: '+91', iso: 'IN', flag: '🇮🇳', name: 'India', nsn: [10] },
  { code: '+1', iso: 'US', flag: '🇺🇸', name: 'United States / Canada', nsn: [10] },
  { code: '+44', iso: 'GB', flag: '🇬🇧', name: 'United Kingdom', nsn: [10] },
  { code: '+61', iso: 'AU', flag: '🇦🇺', name: 'Australia', nsn: [9] },
  { code: '+64', iso: 'NZ', flag: '🇳🇿', name: 'New Zealand', nsn: [8, 9, 10] },
  { code: '+65', iso: 'SG', flag: '🇸🇬', name: 'Singapore', nsn: [8] },
  { code: '+60', iso: 'MY', flag: '🇲🇾', name: 'Malaysia', nsn: [9, 10] },
  { code: '+62', iso: 'ID', flag: '🇮🇩', name: 'Indonesia', nsn: [9, 10, 11, 12] },
  { code: '+66', iso: 'TH', flag: '🇹🇭', name: 'Thailand', nsn: [9] },
  { code: '+81', iso: 'JP', flag: '🇯🇵', name: 'Japan', nsn: [10] },
  { code: '+82', iso: 'KR', flag: '🇰🇷', name: 'South Korea', nsn: [9, 10] },
  { code: '+86', iso: 'CN', flag: '🇨🇳', name: 'China', nsn: [11] },
  { code: '+852', iso: 'HK', flag: '🇭🇰', name: 'Hong Kong', nsn: [8] },
  { code: '+971', iso: 'AE', flag: '🇦🇪', name: 'United Arab Emirates', nsn: [9] },
  { code: '+966', iso: 'SA', flag: '🇸🇦', name: 'Saudi Arabia', nsn: [9] },
  { code: '+974', iso: 'QA', flag: '🇶🇦', name: 'Qatar', nsn: [8] },
  { code: '+973', iso: 'BH', flag: '🇧🇭', name: 'Bahrain', nsn: [8] },
  { code: '+965', iso: 'KW', flag: '🇰🇼', name: 'Kuwait', nsn: [8] },
  { code: '+968', iso: 'OM', flag: '🇴🇲', name: 'Oman', nsn: [8] },
  { code: '+977', iso: 'NP', flag: '🇳🇵', name: 'Nepal', nsn: [10] },
  { code: '+880', iso: 'BD', flag: '🇧🇩', name: 'Bangladesh', nsn: [10] },
  { code: '+94', iso: 'LK', flag: '🇱🇰', name: 'Sri Lanka', nsn: [9] },
  { code: '+975', iso: 'BT', flag: '🇧🇹', name: 'Bhutan', nsn: [8] },
  { code: '+960', iso: 'MV', flag: '🇲🇻', name: 'Maldives', nsn: [7] },
  { code: '+95', iso: 'MM', flag: '🇲🇲', name: 'Myanmar', nsn: [8, 9, 10] },
  { code: '+92', iso: 'PK', flag: '🇵🇰', name: 'Pakistan', nsn: [10] },
  { code: '+93', iso: 'AF', flag: '🇦🇫', name: 'Afghanistan', nsn: [9] },
  { code: '+98', iso: 'IR', flag: '🇮🇷', name: 'Iran', nsn: [10] },
  { code: '+90', iso: 'TR', flag: '🇹🇷', name: 'Türkiye', nsn: [10] },
  { code: '+7', iso: 'RU', flag: '🇷🇺', name: 'Russia / Kazakhstan', nsn: [10] },
  { code: '+49', iso: 'DE', flag: '🇩🇪', name: 'Germany', nsn: [10, 11] },
  { code: '+33', iso: 'FR', flag: '🇫🇷', name: 'France', nsn: [9] },
  { code: '+39', iso: 'IT', flag: '🇮🇹', name: 'Italy', nsn: [9, 10] },
  { code: '+34', iso: 'ES', flag: '🇪🇸', name: 'Spain', nsn: [9] },
  { code: '+31', iso: 'NL', flag: '🇳🇱', name: 'Netherlands', nsn: [9] },
  { code: '+41', iso: 'CH', flag: '🇨🇭', name: 'Switzerland', nsn: [9] },
  { code: '+46', iso: 'SE', flag: '🇸🇪', name: 'Sweden', nsn: [7, 8, 9] },
  { code: '+47', iso: 'NO', flag: '🇳🇴', name: 'Norway', nsn: [8] },
  { code: '+45', iso: 'DK', flag: '🇩🇰', name: 'Denmark', nsn: [8] },
  { code: '+353', iso: 'IE', flag: '🇮🇪', name: 'Ireland', nsn: [9] },
  { code: '+351', iso: 'PT', flag: '🇵🇹', name: 'Portugal', nsn: [9] },
  { code: '+48', iso: 'PL', flag: '🇵🇱', name: 'Poland', nsn: [9] },
  { code: '+43', iso: 'AT', flag: '🇦🇹', name: 'Austria', nsn: [10, 11] },
  { code: '+32', iso: 'BE', flag: '🇧🇪', name: 'Belgium', nsn: [9] },
  { code: '+30', iso: 'GR', flag: '🇬🇷', name: 'Greece', nsn: [10] },
  { code: '+27', iso: 'ZA', flag: '🇿🇦', name: 'South Africa', nsn: [9] },
  { code: '+20', iso: 'EG', flag: '🇪🇬', name: 'Egypt', nsn: [10] },
  { code: '+254', iso: 'KE', flag: '🇰🇪', name: 'Kenya', nsn: [9] },
  { code: '+234', iso: 'NG', flag: '🇳🇬', name: 'Nigeria', nsn: [10] },
  { code: '+55', iso: 'BR', flag: '🇧🇷', name: 'Brazil', nsn: [10, 11] },
  { code: '+52', iso: 'MX', flag: '🇲🇽', name: 'Mexico', nsn: [10] },
  { code: '+54', iso: 'AR', flag: '🇦🇷', name: 'Argentina', nsn: [10] },
  { code: '+972', iso: 'IL', flag: '🇮🇱', name: 'Israel', nsn: [9] },
  { code: '+84', iso: 'VN', flag: '🇻🇳', name: 'Vietnam', nsn: [9, 10] },
  { code: '+63', iso: 'PH', flag: '🇵🇭', name: 'Philippines', nsn: [10] }
];

export const DEFAULT_COUNTRY_CODE = '+91';

const MIN_NSN = 6;
const MAX_NSN = 15;

export const GUEST_NAME_MAX = 80;
export const ID_CARD_MIN = 4;
export const ID_CARD_MAX = 30;
export const EMAIL_MAX = 120;
export const NOTE_MAX = 200;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

export const TIME_SLOTS = [
  { value: '1hr', label: '1 Hour', hours: 1 },
  { value: '3hr', label: '3 Hours', hours: 3 },
  { value: '6hr', label: '6 Hours', hours: 6 },
  { value: '9hr', label: '9 Hours', hours: 9 },
  { value: '12hr', label: '12 Hours', hours: 12 },
  { value: '24hr', label: '24 Hours', hours: 24 }
];

/** Strip everything that is not a digit. */
export const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

export const countryFor = (code) => COUNTRY_CODES.find((c) => c.code === code) || COUNTRY_CODES[0];

/** Longest national number the chosen country accepts — drives the input's maxLength. */
export const maxMobileLength = (countryCode) => {
  const country = countryFor(countryCode);
  return country.nsn?.length ? Math.max(...country.nsn) : MAX_NSN;
};

export function validatePnr(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return 'PNR / Ticket number is required';
  if (!PNR_REGEX.test(raw)) return `PNR must be exactly ${PNR_LENGTH} digits`;
  return null;
}

export function validateMobile(value, countryCode = DEFAULT_COUNTRY_CODE) {
  const digits = digitsOnly(value);
  if (!digits) return 'Mobile number is required';

  const country = COUNTRY_CODES.find((c) => c.code === countryCode);

  if (country?.nsn?.length) {
    if (!country.nsn.includes(digits.length)) {
      return `${country.name} numbers must be ${country.nsn.join(' or ')} digits`;
    }
    if (country.code === '+91' && !/^[6-9]/.test(digits)) {
      return 'Indian mobile numbers must start with 6, 7, 8 or 9';
    }
    return null;
  }

  if (digits.length < MIN_NSN || digits.length > MAX_NSN) {
    return `Mobile number must be between ${MIN_NSN} and ${MAX_NSN} digits`;
  }
  return null;
}

export function validateGuestName(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return 'Guest name is required';
  if (raw.length < 2) return 'Guest name is too short';
  if (raw.length > GUEST_NAME_MAX) return `Guest name must be under ${GUEST_NAME_MAX} characters`;
  return null;
}

export function validateEmail(value, { required = false } = {}) {
  const raw = String(value ?? '').trim();
  if (!raw) return required ? 'Email is required' : null;
  if (raw.length > EMAIL_MAX) return 'Email is too long';
  return EMAIL_REGEX.test(raw) ? null : 'Enter a valid email address';
}

export function validateIdCardNumber(value, { required = false } = {}) {
  const raw = String(value ?? '').trim();
  if (!raw) return required ? 'ID card number is required' : null;
  if (raw.length < ID_CARD_MIN) return `ID card number must be at least ${ID_CARD_MIN} characters`;
  if (raw.length > ID_CARD_MAX) return `ID card number must be under ${ID_CARD_MAX} characters`;
  if (!/^[A-Za-z0-9\- /]+$/.test(raw)) return 'ID card number may only contain letters, digits, spaces, - and /';
  return null;
}

/**
 * Validate the whole guest section in one pass.
 * Returns a `{ field: message }` map — empty when everything is acceptable.
 */
export function validateBookingForm(form, { requireCategory = true, requireRoom = false, requireSchedule = false } = {}) {
  const errors = {};

  const name = validateGuestName(form.guestName);
  if (name) errors.guestName = name;

  const pnr = validatePnr(form.pnr);
  if (pnr) errors.pnr = pnr;

  const mobile = validateMobile(form.mobile, form.countryCode);
  if (mobile) errors.mobile = mobile;

  const email = validateEmail(form.email);
  if (email) errors.email = email;

  if (form.idInputType === 'image') {
    if (!form.idCardImage) errors.idProof = 'Please upload or capture an ID card image';
  } else {
    const id = validateIdCardNumber(form.idCardNumber, { required: true });
    if (id) errors.idProof = id;
  }

  if (requireCategory && !form.categoryId) errors.categoryId = 'Please select a room category';
  if (requireRoom && !form.roomId) errors.roomId = 'Please select a room for immediate check-in';
  if (requireSchedule) {
    if (!form.scheduledDate) errors.scheduledDate = 'Please pick a check-in date';
    else if (!form.scheduledTime) errors.scheduledTime = 'Please pick a check-in time';
  }

  return errors;
}

/** Display form used on invoices, emails and lists: "+91 9876543210". */
export const formatPhone = (countryCode, mobile) =>
  `${countryCode || DEFAULT_COUNTRY_CODE} ${String(mobile ?? '').trim()}`.trim();
